import styled from "@emotion/styled"
import stringify from "qs-stringify"
import React, { useContext } from "react"
import tw from "twin.macro"
import focalPointTransformer from "../utils/focalPointTransformer"
import PropTypes from "prop-types"
import ThemeContext from "../context/theme-context"

const MediaBox = styled.div`
  ${tw`relative overflow-hidden`}
  ${(props) => {
    if (!props.cover) {
      return `
        width: 100%;
        height: 0;
        padding-bottom: ${
          props.cropRatio
            ? `${props.cropRatio * 100}%`
            : `${(props.imageHeight / props.imageWidth) * 100}%`
        };
      `
    }
    if (props.cover) {
      return `
        width: 100%;
        height: 100%;
        object-fit: cover;
        font-family: "blur-up: auto";
      `
    }
  }}
  .lazyload-image ,
  .ls-blur-up-img {
    ${tw`block absolute inset-0 w-full h-full object-cover`}
    font-family: "blur-up: auto";
    ${(props) => {
      if (props.focalPoint) {
        return `
          object-position: ${props.focalPoint.string};
        `
      }
    }}
  }
  .lazyload-image.ls-blur-up-is-loading,
  .lazyload-image.lazyload:not([src]) {
    visibility: hidden;
  }
  .ls-blur-up-img {
    z-index: 1;
    filter: blur(10px);
    opacity: 1;
    transition: opacity 300ms, filter 450ms;
  }
  .ls-blur-up-img.ls-inview.ls-original-loaded {
    opacity: 0;
    filter: blur(5px);
  }
`
const Picture = ({
  image,
  alt,
  cropWidth,
  cropRatio,
  className,
  cover,
  focalPoint,
  ...otherProps
}) => {
  focalPoint = focalPointTransformer(focalPoint)
  const { hydrated: isClient } = useContext(ThemeContext)
  const params = {
    w: cropWidth ? cropWidth : ``,
    h: cropWidth && cropRatio ? cropWidth * cropRatio : ``,
    fit: cropRatio ? "crop" : ``,
    q: 85,
  }
  const paramsLow = {
    w: 100,
    h: cropRatio ? 100 * cropRatio : ``,
    fit: cropRatio ? "crop" : ``,
    q: 85,
  }
  const params2x = {
    w: cropWidth ? cropWidth : ``,
    h: cropWidth && cropRatio ? cropWidth * cropRatio : ``,
    fit: cropRatio ? "crop" : ``,
    q: 50,
    dpr: 2,
  }
  const paramsString = stringify(params)
  const paramsLowString = stringify(paramsLow)
  const params2xString = stringify(params2x)

  return (
    <MediaBox
      imageWidth={image.width}
      imageHeight={image.height}
      className={className}
      cropRatio={cropRatio}
      cover={cover}
      focalPoint={focalPoint}
    >
      <picture>
        <source
          src={`${image.url}?${paramsLowString}&fm=webp`}
          data-lowsrc={`${image.url}?${paramsLowString}&fm=webp`}
          data-srcset={`${image.url}?${paramsString}&fm=webp, ${image.url}?${params2xString}&fm=webp 2x`}
          type="image/webp"
        />
        <img
          src={`${image.url}?${paramsLowString}&fm=jpg&auto=format`}
          data-lowsrc={`${image.url}?${paramsLowString}&fm=jpg&auto=format`}
          data-src={`${image.url}?${paramsString}&fm=jpg&auto=format`}
          data-srcset={`${image.url}?${paramsString}&fm=jpg&auto=format, ${image.url}?${params2xString}&fm=jpg&auto=format 2x`}
          className={`lazyload-image ${ isClient ? 'lazyload' : ''}`}
          alt={alt || ` `}
          data-object-fit={cover}
          {...otherProps}
        />
      </picture>
    </MediaBox>
  )
}

Picture.propTypes = {
  global: PropTypes.object,
  image: PropTypes.object,
  alt: PropTypes.string,
  cropWidth: PropTypes.number,
  cropRatio: PropTypes.number,
  className: PropTypes.string,
  cover: PropTypes.bool,
  focalPoint: PropTypes.string,
}

Picture.defaultProps = {
  focalPoint: `0.5,0.5`,
}

export default Picture
